<template>
    <v-container fluid>
      <v-card elevation="2">
        <v-card-title class="d-flex justify-space-between align-center">
          <div class="text-h6">Companies</div>
          <v-spacer/>
          <v-text-field
              v-model="search"
              label="Search (name / email / phone / industry)"
              prepend-inner-icon="mdi-magnify"
              density="compact"
              variant="solo-filled"
              flat hide-details single-line
              class="me-4"
          />
          <v-btn color="primary" @click="openDialog" v-if="authStore.canEdit">
            <v-icon left>mdi-plus</v-icon> Add Company
          </v-btn>
          <!-- <ExportButtons entity="companies" filename="Companies" :exclude="['id','created_at','updated_at', 'status']"></ExportButtons> -->
          <ExportButtons entity="companies" filename="Companies" :headers="headers"></ExportButtons>
        </v-card-title>
  
        <v-divider></v-divider>

        <v-data-table 
          :headers="headers"
          :items="filtered"
          :search="search"
          item-value="id"
          class="elevation-1"
          :loading="tableLoading"
          loading-text="Loading companies... Please wait"
          hover
          show-expand
          v-model:expanded="expanded"
        >
        <template v-slot:expanded-row="{ columns, item }">
          <tr>
            <td :colspan="columns.length">
              <v-card flat class="pa-3 my-2" color="grey-lighten-5">
                <v-card-subtitle class="pb-1 font-weight-bold">Description:</v-card-subtitle>
                <v-card-text class="text-body-2" style="white-space: pre-wrap;">
                    {{ item.description || 'No description provided.' }}
                </v-card-text>
                </v-card>
            </td>
          </tr>
        </template>

        <template v-slot:[`item.status`]="{ item }">
          <v-chip :color="item.status ? 'success' : 'error'" label small>
            {{ item.status ? 'Active' : 'Inactive' }}
          </v-chip>
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <v-tooltip location="top" text="Edit Company">
            <template v-slot:activator="{ props }">
              <v-icon v-bind="props" small class="me-1" @click="editCompany(item)" color="secondary">
                mdi-pencil
              </v-icon>
            </template>
          </v-tooltip>

          <v-tooltip location="top" text="Delete Company">
            <template v-slot:activator="{ props }">
              <v-icon v-bind="props" small class="me-1" @click="deleteItemPrompt(item)" color="red">
                mdi-delete
              </v-icon>
            </template>
          </v-tooltip>
        </template>

        <template v-slot:[`no-data`]>
            <v-alert :value="true" color="info" icon="mdi-information-variant" class="ma-3">
              {{ tableLoading ? 'Fetching data...' : 'No companies available or match your search.' }}
            </v-alert>
        </template>
      </v-data-table>

        <v-dialog v-model="dialog" max-width="600px">
          <v-card>
            <v-card-title>{{ editedIndex === -1 ? 'Add' : 'Edit' }} Company</v-card-title>
            <v-card-text>
              <v-text-field label="Name" v-model="editedItem.name" required></v-text-field>
              <v-text-field label="Email" v-model="editedItem.email"></v-text-field>
              <v-text-field label="Phone" v-model="editedItem.phone_number"></v-text-field>
              <v-text-field label="Website" v-model="editedItem.website"></v-text-field>
              <v-text-field label="Address" v-model="editedItem.address"></v-text-field>
              <v-text-field label="Industry" v-model="editedItem.industry_type"></v-text-field>
              <v-select
                label="Currency"
                v-model="editedItem.currency_code"
                :items="currencies"
                item-title="currency_name"
                item-value="currency_code"
              />
              <v-select
                label="Is Active"
                v-model="editedItem.status"
                :items="[{name: 'Active', id: true}, {name: 'Inactive', id: false}]"
                item-title="name"
                item-value="id"
                :default="true"
              />
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn @click="closeDialog">Cancel</v-btn>
              <v-btn color="primary" variant="elevated" @click="saveCompany">Save</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <v-dialog v-model="deleteDialog" max-width="500px">
        <v-card :loading="dialogLoading">
          <v-card-title class="text-h5">Confirm Deletion</v-card-title>
          <v-card-text>Are you sure you want to delete this company: {{ itemToDelete?.name }}?</v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="blue-darken-1" text @click="closeDeleteDialog" :disabled="dialogLoading">Cancel</v-btn>
            <v-btn color="red-darken-1" text @click="confirmDelete" :loading="dialogLoading">Delete</v-btn>
            <v-spacer></v-spacer>
          </v-card-actions>
        </v-card>
      </v-dialog>

      </v-card>
    </v-container>
  </template>
  
  <script setup>
  import { ref, onMounted, computed } from 'vue';
  // import apiClient from '@/services/api';
  import { useAuthStore } from '@/stores/auth';
  import ExportButtons from '@/components/ExportButtons.vue';
  import CompanyService from '@/services/CompaniesService';
  import { useSnackbar } from '@/services/genFunctions';
  import CurrenciesService from '@/services/CurrenciesService';
  import { nextTick } from 'vue';

  const { showSnackbar } = useSnackbar();
  const currencies = ref([]);
  const dialog = ref(false);
  const editedIndex = ref(-1);
  const editedItem = ref({ status: true });
  const companies = ref([]);
  const authStore = useAuthStore();
  const search = ref('');
  const tableLoading = ref(true);
  const deleteDialog = ref(false);
  const dialogLoading = ref(false);
  const itemToDelete = ref(null);
  const expanded = ref([]);

  const headers = [
    { title: 'Name', key: 'name' },
    { title: 'Email', key: 'email' },
    { title: 'Phone', key: 'phone_number' },
    { title: 'Industry', key: 'industry_type' },
    { title: 'Currency', key: 'currency_code' },
    { title: 'Is Active', key: 'status' },
    { title: 'Actions', key: 'actions', sortable: false }
  ];

  const loadCurrencies = async () => {
    const data = await CurrenciesService.getCurrencies();
    currencies.value = data;
  };

  const load = async () => {
    tableLoading.value = true;
    try {
      const data = await CompanyService.getCompanies();
      companies.value = data;
    } catch (error) {
      console.error('Error loading companies:', error);
      showSnackbar('Failed to load companies', 'error');
    } finally {
      tableLoading.value = false;
    }
  };
  onMounted(async () => {
    await loadCurrencies();
    await load();
  });

  const filtered = computed(() => {
    const filteredData = companies.value.filter(c =>
      (c.name+c.email+c.phone_number+c.industry_type).toLowerCase().includes(search.value.toLowerCase())
    );
    return filteredData;
  });

  function openDialog() {
    editedIndex.value = -1;
    editedItem.value = { status: true };
    dialog.value = true;
  }
  
  function editCompany(item) {
    editedIndex.value = item.id;
    editedItem.value = { ...item };
    dialog.value = true;
  }
  
  function closeDialog() {
    dialog.value = false;
  }
  
  async function saveCompany() {
    if (editedIndex.value === -1) {
      await CompanyService.createCompany(editedItem.value);
    } else {
      await CompanyService.updateCompany(editedIndex.value, editedItem.value);
    }
    await load();
    closeDialog();
  }
  
  const deleteItemPrompt = (item) => {
    itemToDelete.value = { ...item };
    deleteDialog.value = true;
  };

  const closeDeleteDialog = () => {
    deleteDialog.value = false;
    nextTick(() => itemToDelete.value = null);
    dialogLoading.value = false;
  };

  const confirmDelete = async () => {
    if (!itemToDelete.value?.id) return;
    dialogLoading.value = true;
    try {
        await CompanyService.deleteCompany(itemToDelete.value.id);
        companies.value = companies.value.filter(c => c.id !== itemToDelete.value.id);
        showSnackbar('Company deleted successfully!', 'success');
        closeDeleteDialog();
    } catch (error) {
        const errorMsg = error.response?.data?.error || 'Could not delete company.';
        showSnackbar(errorMsg, 'error', 6000);
        console.error("Error deleting company:", error.response || error);
    } finally {
        dialogLoading.value = false;
    }
  };
  
  </script>
  
  <style scoped>
  .v-data-table {
    margin-top: 20px;
  }
  </style>
  